import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Play, Radio, Tv, Video } from 'lucide-react';
import { mediaCenterCards } from '@/content/public-site-content';
import { apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth/store';

interface MediaListResponse {
  media: MediaItem[];
}

interface MediaItem {
  id: number;
  title: string;
  description: string | null;
  thumbnailUrl: string | null;
  uploadStatus: 'pending' | 'processing' | 'ready' | 'failed';
  createdAt: string;
}

export default function MediaPage() {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    async function loadMedia() {
      try {
        const headers: Record<string, string> = {};
        if (isAuthenticated) {
          const token = useAuthStore.getState().token;
          if (token) headers.Authorization = `Bearer ${token}`;
        }

        const response = await apiClient.get<MediaListResponse>('/media', { headers, timeout: 3000 });
        if (response.success && response.data?.media) {
          setMedia(response.data.media.slice(0, 4));
        }
      } finally {
        setLoading(false);
      }
    }

    loadMedia();
  }, [isAuthenticated]);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <section className="rounded-[2rem] border border-border/70 bg-card/90 p-8 shadow-sm">
        <div className="space-y-4 text-center">
          <Badge variant="outline" className="mx-auto w-fit border-primary/30 bg-primary/5">
            <Play className="mr-1 h-3 w-3 text-primary" />
            Media Center
          </Badge>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Media Center</h1>
          <p className="mx-auto max-w-2xl text-muted-foreground">
            Video on demand, audio on demand, GMTV, and radio access points collected in one place.
          </p>
        </div>
      </section>

      <section className="grid gap-6 md:grid-cols-2">
        {mediaCenterCards.map((item) => (
          <Card key={item.title} className="overflow-hidden border-border/70 bg-card/95 py-0 shadow-sm">
            <div className="h-56 overflow-hidden bg-muted">
              <img src={item.image} alt={item.title} className="h-full w-full object-cover" />
            </div>
            <CardContent className="space-y-4 p-6">
              <div className="space-y-2">
                <h2 className="text-xl font-semibold">{item.title}</h2>
                <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
              </div>
              <Button asChild>
                <Link to={item.href}>{item.cta}</Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="rounded-[2rem] border border-primary/20 bg-primary/5 p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background">
            <Tv className="h-7 w-7 text-primary" />
          </div>
          <div className="space-y-1">
            <h2 className="text-2xl font-semibold">Global Missions TV (GMTV)</h2>
            <p className="text-sm text-muted-foreground">
              Watch the GMTV rollout and follow upcoming global gospel media programming.
            </p>
          </div>
          <Button className="md:ml-auto" asChild>
            <Link to="/features/gmtv">Watch</Link>
          </Button>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Video className="h-5 w-5 text-primary" />
          <h2 className="text-2xl font-semibold">Latest uploads</h2>
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading media content...</p>
        ) : media.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No latest uploads are available yet. Use the media destinations above to watch and listen.
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {media.map((item) => (
              <Card key={item.id} className="border-border/70 bg-card/90">
                <CardContent className="space-y-3 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-semibold">{item.title}</h3>
                    <Badge variant="outline">{item.uploadStatus}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {item.description || 'Recent media upload from the ministry archive.'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-dashed border-border/70 bg-card/50 p-6 text-center">
        <Radio className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h3 className="mb-1 text-lg font-semibold">Audio availability</h3>
        <p className="mx-auto max-w-xl text-sm text-muted-foreground">
          Audio on demand and radio access can vary by platform. Use the dedicated destinations above for the best available experience.
        </p>
      </section>
    </div>
  );
}
